import type { ReactElement, ReactNode } from 'react';

export interface MarkdownContentProps {
  content: string;
  className?: string;
  testId?: string;
}

const URL_PATTERN = /https?:\/\/[^\s`)\]]+/;

function trimUrlTail(url: string): { url: string; len: number } {
  const match = /[.,;:!?)\]]+$/.exec(url);
  if (!match) return { url, len: url.length };
  return { url: url.slice(0, match.index), len: match.index };
}

function MarkdownLink({
  href,
  children,
  className,
}: {
  href: string;
  children: ReactNode;
  className?: string;
}): ReactElement {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className={className ?? 'text-crucible-accent-hover underline-offset-2 hover:underline'}
    >
      {children}
    </a>
  );
}

function renderInline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let i = 0;
  let buffer = '';
  let key = 0;

  const flush = (): void => {
    if (!buffer) return;
    const segments = buffer.split('\n');
    segments.forEach((segment, index) => {
      if (index > 0) out.push(<br key={`br-${key++}`} />);
      if (segment) out.push(segment);
    });
    buffer = '';
  };

  while (i < text.length) {
    if (text[i] === '`') {
      const end = text.indexOf('`', i + 1);
      if (end > i) {
        flush();
        const inner = text.slice(i + 1, end);
        const codeClass =
          'rounded-md bg-white/[0.08] px-1.5 py-0.5 font-mono text-[13px] text-white/80';
        if (/^https?:\/\/\S+$/.test(inner)) {
          out.push(
            <MarkdownLink key={key++} href={inner} className={`${codeClass} hover:underline`}>
              {inner}
            </MarkdownLink>,
          );
        } else {
          out.push(
            <code key={key++} className={codeClass}>
              {inner}
            </code>,
          );
        }
        i = end + 1;
        continue;
      }
    }

    if (text[i] === '*' && text[i + 1] === '*') {
      const end = text.indexOf('**', i + 2);
      if (end > i + 1) {
        flush();
        out.push(<strong key={key++}>{renderInline(text.slice(i + 2, end))}</strong>);
        i = end + 2;
        continue;
      }
    }

    if (text[i] === '*' && text[i + 1] !== '*' && text[i + 1] !== ' ') {
      const end = text.indexOf('*', i + 1);
      if (end > i && text[end - 1] !== ' ') {
        flush();
        out.push(<em key={key++}>{renderInline(text.slice(i + 1, end))}</em>);
        i = end + 1;
        continue;
      }
    }

    if (text[i] === 'h') {
      const match = URL_PATTERN.exec(text.slice(i));
      if (match && match.index === 0) {
        const trimmed = trimUrlTail(match[0]);
        if (trimmed.url.length > 8) {
          flush();
          out.push(
            <MarkdownLink key={key++} href={trimmed.url}>
              {trimmed.url}
            </MarkdownLink>,
          );
          i += trimmed.len;
          continue;
        }
      }
    }

    buffer += text[i];
    i += 1;
  }

  flush();
  return out;
}

function renderHeading(block: string, key: number): ReactElement | null {
  const match = /^(#{1,3})\s+(.+)$/.exec(block);
  if (!match) return null;

  const level = match[1]!.length;
  const text = match[2]!;
  if (level === 1) {
    return (
      <h3 key={key} className="text-base font-semibold text-white">
        {renderInline(text)}
      </h3>
    );
  }
  if (level === 2) {
    return (
      <h4 key={key} className="text-sm font-semibold text-white">
        {renderInline(text)}
      </h4>
    );
  }
  return (
    <h5 key={key} className="text-xs font-semibold uppercase text-white/70">
      {renderInline(text)}
    </h5>
  );
}

function MarkdownProse({ text }: { text: string }): ReactElement {
  const blocks = text.split(/\n{2,}/).filter((block) => block.trim().length > 0);

  return (
    <>
      {blocks.map((block, index) => {
        const trimmed = block.trim();
        const heading = renderHeading(trimmed, index);
        if (heading) return heading;

        const lines = trimmed.split('\n');
        const isUnorderedList = lines.every((line) => /^\s*[-*]\s+/.test(line));
        if (isUnorderedList) {
          return (
            <ul key={index} className="list-disc space-y-1 pl-5">
              {lines.map((line, lineIndex) => (
                <li key={lineIndex}>{renderInline(line.replace(/^\s*[-*]\s+/, ''))}</li>
              ))}
            </ul>
          );
        }

        const isOrderedList = lines.every((line) => /^\s*\d+\.\s+/.test(line));
        if (isOrderedList) {
          return (
            <ol key={index} className="list-decimal space-y-1 pl-5">
              {lines.map((line, lineIndex) => (
                <li key={lineIndex}>{renderInline(line.replace(/^\s*\d+\.\s+/, ''))}</li>
              ))}
            </ol>
          );
        }

        return (
          <p key={index} className="whitespace-pre-wrap">
            {renderInline(trimmed)}
          </p>
        );
      })}
    </>
  );
}

export function MarkdownContent({
  content,
  className,
  testId,
}: MarkdownContentProps): ReactElement {
  const parts = content.split(/(```[\s\S]*?```)/g);

  // `overflow-wrap: anywhere` (inherited, so inline <code> picks it up too)
  // breaks long unbreakable tokens — file paths, URLs, IDs — that would
  // otherwise push the message past the panel width and make the chat scroll
  // horizontally. `min-w-0` lets the block shrink inside its flex ancestors.
  // Fenced ``` blocks keep their own `overflow-x-auto` (white-space: pre, so
  // they don't wrap — they scroll internally instead).
  return (
    <div
      data-testid={testId}
      className={['min-w-0 [overflow-wrap:anywhere]', className].filter(Boolean).join(' ')}
    >
      {parts.map((part, index) => {
        if (part.startsWith('```') && part.endsWith('```')) {
          const inner = part
            .slice(3, -3)
            .replace(/^[\w-]+\n/, '')
            .replace(/\n$/, '');
          return (
            <pre
              key={index}
              className="min-w-0 overflow-x-auto rounded-xl bg-white/[0.06] p-4 font-mono text-[13px] leading-relaxed text-white/75"
            >
              <code>{inner}</code>
            </pre>
          );
        }
        return <MarkdownProse key={index} text={part} />;
      })}
    </div>
  );
}
